import { useRef, type ReactElement } from 'react';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import type { EntityTypeDto } from '@gr/core/content/entities/dto';
import { FIELD_TYPES, FieldType, ReferenceTargetKind } from '@gr/core/content/entities/fields';
import { useT } from '../../../../app/di';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Button, IconButton } from '../../../../ui-kit/Button/Button';
import { useConfirm } from '../../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { SelectField, TextArea } from '../../../../ui-kit/Field/Field';
import { FieldRow, FieldRowActions, FieldRowGrow } from '../../../../ui-kit/FieldRow/FieldRow';
import { FormCheckbox, FormSelectField, FormTextField, useFormMessage } from '../../../../ui-kit/form/form';
import { keyForLabel, newFieldRow, reconcileTitleRowId, retype, type FieldsFormValues } from '../model/fields-form';
import styles from './FieldsEditor.module.scss';

export interface FieldsEditorProps {
  form: UseFormReturn<FieldsFormValues>;
  /** Типы, на записи которых может ссылаться поле. */
  entityTypes: readonly EntityTypeDto[];
  /** Сколько записей потеряют значения удалённого сохранённого поля. */
  affectedCount: number;
  /** Следить, чтобы поле-заголовок указывало на существующее текстовое поле. */
  tracksTitle: boolean;
}

/** ENT-UC-1, ENT-UC-2, ENT-UC-4: конструктор полей. Строки — useFieldArray, ключ строки — field.id. */
export function FieldsEditor({ form, entityTypes, affectedCount, tracksTitle }: FieldsEditorProps): ReactElement {
  const { t } = useT();
  const confirm = useConfirm();
  const message = useFormMessage();
  const { control, getValues, setValue, watch, formState } = form;
  const { fields, append, remove, move } = useFieldArray({ control, name: 'fields' });
  const rows = watch('fields');
  const nextRow = useRef(0);
  const disabled = formState.isSubmitting;

  const takenKeys = (exceptIndex: number): string[] => getValues('fields').flatMap((row, i) => (i === exceptIndex ? [] : [row.key]));
  const syncTitle = (): void => {
    if (tracksTitle) setValue('titleRowId', reconcileTitleRowId(getValues()));
  };

  // ENT-RULE-2: ключ нового поля следует за подписью; строка и ссылка на неё остаются прежними.
  const relabel = (index: number, label: string): void => {
    const key = keyForLabel(getValues(`fields.${index}`), label, takenKeys(index));
    if (key !== null) setValue(`fields.${index}.key`, key);
  };

  const changeType = (index: number, type: FieldType): void => {
    const patch = retype(getValues(`fields.${index}`), type);
    setValue(`fields.${index}.options`, patch.options);
    setValue(`fields.${index}.target`, patch.target);
    setValue(`fields.${index}.multiple`, patch.multiple);
    syncTitle();
  };

  const add = (): void => {
    nextRow.current += 1;
    append(newFieldRow(`new:${nextRow.current}`, takenKeys(-1)));
    syncTitle();
  };

  const removeRow = async (index: number): Promise<void> => {
    if (getValues(`fields.${index}.existing`) && affectedCount > 0) {
      const ok = await confirm({ title: t('entities.editor.removeField'), text: t('entities.editor.removeWarning', { n: affectedCount }), confirmLabel: t('common.delete'), danger: true });
      if (!ok) return;
    }
    remove(index);
    syncTitle();
  };

  const targetOptions = [
    ...entityTypes.map((type) => ({ value: `${ReferenceTargetKind.Entity}:${type.id}`, label: type.name })),
    { value: ReferenceTargetKind.Post, label: t('entities.editor.targetPosts') },
  ];
  // Ошибка всего списка («fields» без номера строки) — от сервера.
  const listError = message(formState.errors.fields?.root ?? formState.errors.fields);

  return (
    <div className="u-stack">
      {fields.length === 0 ? <p className="u-muted">{t('entities.editor.noFields')}</p> : null}
      {listError ? <Alert tone="danger">{listError}</Alert> : null}
      <ol className={styles.list}>
        {fields.map((field, index) => {
          const row = rows[index] ?? field;
          const rowError = message(formState.errors.fields?.[index]);
          return (
            <li key={field.id} className={styles.item}>
              <FieldRow>
                <FieldRowGrow>
                  <FormTextField
                    control={control}
                    name={`fields.${index}.label`}
                    label={t('entities.editor.label')}
                    hint={t('entities.editor.key', { key: row.key })}
                    disabled={disabled}
                    onValueChange={(label) => relabel(index, label)}
                  />
                </FieldRowGrow>
                <FormSelectField
                  control={control}
                  name={`fields.${index}.type`}
                  label={t('entities.editor.type')}
                  disabled={disabled || row.existing}
                  hint={row.existing ? t('entities.editor.typeLocked') : undefined}
                  options={FIELD_TYPES.map((type) => ({ value: type, label: t(`entities.fieldTypes.${type}`) }))}
                  onValueChange={(type) => changeType(index, type as FieldType)}
                />
                <FieldRowActions>
                  <IconButton icon="up" label={t('common.moveUp')} size="sm" onClick={() => move(index, index - 1)} disabled={disabled || index === 0} />
                  <IconButton icon="down" label={t('common.moveDown')} size="sm" onClick={() => move(index, index + 1)} disabled={disabled || index === fields.length - 1} />
                  <IconButton icon="trash" label={t('entities.editor.removeField')} size="sm" disabled={disabled} onClick={() => void removeRow(index)} />
                </FieldRowActions>
              </FieldRow>
              {rowError ? <Alert tone="danger">{rowError}</Alert> : null}
              <div className="u-row">
                <FormCheckbox control={control} name={`fields.${index}.required`} label={t('entities.editor.required')} disabled={disabled} />
                {row.type === FieldType.Select || row.type === FieldType.Reference ? (
                  <FormCheckbox control={control} name={`fields.${index}.multiple`} label={t('entities.editor.multiple')} disabled={disabled} />
                ) : null}
              </div>
              {row.type === FieldType.Select ? (
                <TextArea
                  label={t('entities.editor.options')}
                  rows={3}
                  value={(row.options ?? []).join('\n')}
                  disabled={disabled}
                  onChange={(e) => setValue(`fields.${index}.options`, e.target.value.split('\n'), { shouldDirty: true })}
                />
              ) : null}
              {row.type === FieldType.Reference ? (
                <SelectField
                  label={t('entities.editor.target')}
                  disabled={disabled || row.existing}
                  value={row.target?.kind === ReferenceTargetKind.Entity ? `${ReferenceTargetKind.Entity}:${row.target.entityTypeId}` : ReferenceTargetKind.Post}
                  options={targetOptions}
                  onChange={(e) => {
                    const [kind, entityTypeId] = e.target.value.split(':');
                    setValue(
                      `fields.${index}.target`,
                      kind === ReferenceTargetKind.Entity && entityTypeId ? { kind: ReferenceTargetKind.Entity, entityTypeId } : { kind: ReferenceTargetKind.Post },
                      { shouldDirty: true },
                    );
                  }}
                />
              ) : null}
            </li>
          );
        })}
      </ol>
      <div>
        <Button icon="plus" onClick={add} disabled={disabled}>
          {t('entities.editor.addField')}
        </Button>
      </div>
    </div>
  );
}
